import { useEffect, useMemo, useRef } from 'react'
import styled from 'styled-components'
import * as echarts from 'echarts'
import { ITimeDataItem } from '../../util/algorithm/algoInterface'

type CalDataPoint = {
  [month: number]: number[]
}

interface Props {
  originalData?: ITimeDataItem[] | CalDataPoint
  essData?: ITimeDataItem[]
  kepcoData?: ITimeDataItem[]
  month: number
  width?: string
  height?: string
  selectedChartDataType?: string
}

interface WrapperProps {
  height?: string
  width?: string
}

const Wrapper = styled.div<WrapperProps>`
  display: flex;
  flex-direction: column;
  align-items: center;
  height: ${(props) => props.height || '100%'};
  width: ${(props) => props.width || '100%'};
  padding: 0;
  flex-shrink: 0;
`

const hourLabel = Array.from({ length: 24 }, (_, index) => `${index}`)

const getMonthDate = (item: ITimeDataItem) => new Date(item.date).getMonth() + 1

const formatDateLabel = (date: string) => date.slice(2, 16).replace('T', ' ')

const toMonthSeries = (
  data: ITimeDataItem[] | undefined,
  month: number,
  selectedChartDataType?: string
) => {
  if (!data || data.length === 0) return []

  const monthData = data.filter((item) => getMonthDate(item) === month)

  if (selectedChartDataType === 'realTime') {
    return monthData.map((item) => item.value || 0)
  }

  const sumByHour = Array(24).fill(0)
  const countByHour = Array(24).fill(0)

  monthData.forEach((item) => {
    const hour = item.hour ?? new Date(item.date).getHours()
    sumByHour[hour] += item.value || 0
    countByHour[hour] += 1
  })

  return sumByHour.map((sum, index) =>
    countByHour[index] === 0 ? 0 : sum / countByHour[index]
  )
}

const toOriginalSeries = (
  data: ITimeDataItem[] | CalDataPoint | undefined,
  month: number,
  selectedChartDataType?: string
) => {
  if (!data) return { labels: [] as string[], values: [] as number[] }

  if (Array.isArray(data)) {
    const monthData = data.filter((item) => getMonthDate(item) === month)

    if (selectedChartDataType === 'realTime') {
      return {
        labels: monthData.map((item) => formatDateLabel(item.date)),
        values: monthData.map((item) => item.value || 0),
      }
    }

    return {
      labels: hourLabel,
      values: toMonthSeries(data, month, selectedChartDataType),
    }
  }

  return {
    labels: hourLabel,
    values: Array.isArray(data[month]) ? data[month] : [],
  }
}

const alignLength = (values: number[], length: number) =>
  Array.from({ length }, (_, index) => values[index] || 0)

const PowerSourceStackedAreaChart = ({
  originalData,
  essData,
  kepcoData,
  month,
  width,
  height,
  selectedChartDataType,
}: Props) => {
  const chartRef = useRef<echarts.ECharts | null>(null)
  const chartId = useRef(
    `power-source-chart-${Math.floor(Math.random() * 1000000)}`
  )

  const chartData = useMemo(() => {
    const original = toOriginalSeries(
      originalData,
      month,
      selectedChartDataType
    )
    const labels =
      original.labels.length > 0
        ? original.labels
        : selectedChartDataType === 'realTime'
          ? []
          : hourLabel
    const valueLength = labels.length
    const ess = alignLength(
      toMonthSeries(essData, month, selectedChartDataType),
      valueLength
    )
    const kepco = alignLength(
      toMonthSeries(kepcoData, month, selectedChartDataType),
      valueLength
    )

    return {
      labels,
      original: alignLength(original.values, valueLength),
      ess,
      kepco,
    }
  }, [originalData, essData, kepcoData, month, selectedChartDataType])

  useEffect(() => {
    const chartDom = document.getElementById(chartId.current)
    if (!chartDom) return

    if (chartRef.current) {
      chartRef.current.dispose()
    }

    const chart = echarts.init(chartDom)
    const option: echarts.EChartsOption = {
      color: ['#7E90A6', '#57C9A7', '#2F3A4A'],
      tooltip: {
        trigger: 'axis',
        axisPointer: {
          type: 'cross',
          label: {
            backgroundColor: '#6a7985',
          },
        },
        valueFormatter: (value) =>
          `${Number(value || 0).toLocaleString(undefined, {
            maximumFractionDigits: 2,
          })} kWh`,
      },
      legend: {
        type: 'plain',
        top: 8,
        left: 'center',
        orient: 'horizontal',
        data: [
          { name: '한전에게 사올 전력', icon: 'rect' },
          { name: 'ESS에게 사올 전력', icon: 'rect' },
          '기존 수용가 패턴',
        ],
        itemWidth: 14,
        itemHeight: 10,
        itemGap: 18,
        textStyle: {
          color: '#4D4D4D',
          fontFamily: 'SUIT',
          fontSize: 12,
        },
      },
      grid: {
        top: 64,
        left: '3%',
        right: '3%',
        bottom: '20%',
        containLabel: true,
      },
      yAxis: {
        type: 'value',
        scale: true,
        name: 'kWh',
        nameLocation: 'end',
        nameTextStyle: {
          align: 'right',
          fontWeight: 'bold',
          color: '#666',
        },
        splitLine: {
          lineStyle: {
            color: '#E6E6E6',
            type: 'dashed',
          },
        },
      },
      xAxis: {
        type: 'category',
        boundaryGap: false,
        data: chartData.labels,
      },
      graphic: {
        type: 'text',
        left: 20,
        bottom: 12,
        style: {
          text: '기간 확대',
          fill: '#4D4D4D',
          fontFamily: 'SUIT',
          fontSize: 12,
          fontWeight: 600,
        },
      },
      dataZoom: [
        {
          type: 'slider',
          left: 88,
          right: 24,
          bottom: 8,
        },
      ],
      series: [
        {
          name: '한전에게 사올 전력',
          data: chartData.kepco,
          type: 'line',
          stack: 'total',
          smooth: true,
          showSymbol: false,
          lineStyle: {
            width: 1.5,
          },
          areaStyle: {
            opacity: 0.42,
          },
          emphasis: {
            focus: 'series',
          },
          animation: false,
        },
        {
          name: 'ESS에게 사올 전력',
          data: chartData.ess,
          type: 'line',
          stack: 'total',
          smooth: true,
          showSymbol: false,
          lineStyle: {
            width: 1.5,
          },
          areaStyle: {
            opacity: 0.52,
          },
          emphasis: {
            focus: 'series',
          },
          animation: false,
        },
        {
          name: '기존 수용가 패턴',
          data: chartData.original,
          type: 'line',
          smooth: true,
          showSymbol: false,
          z: 4,
          lineStyle: {
            width: 2.5,
            color: '#2F3A4A',
          },
          animation: false,
        },
      ],
    }

    const handleResize = () => {
      chart.resize()
    }

    window.addEventListener('resize', handleResize)
    chart.setOption(option)
    chartRef.current = chart

    return () => {
      chart.dispose()
      window.removeEventListener('resize', handleResize)
    }
  }, [chartData])

  return (
    <Wrapper height={height} width={width}>
      <div id={chartId.current} style={{ height, width }} />
    </Wrapper>
  )
}

export default PowerSourceStackedAreaChart
